import { apiGet, apiPost } from '../api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryCacheKeys } from '@/api/queryKeys';

// 세션 상태 Enum
export type SessionStatus = 
  | 'INITIALIZING'          // 세션 생성 및 디바이스 연결 준비 중
  | 'PENDING_DEVICE_WAKEUP' // 디바이스 깨어남 대기 (헬스체크 5회 실패 후 주기 시간만큼 대기)
  | 'IN_PROGRESS'           // BCG 데이터 수집 진행 중
  | 'COMPLETED'             // 정상 완료
  | 'CANCELLED'             // 사용자 중도 취소
  | 'FAILED'                // 일반 에러
  | 'ABORTED_TIMEOUT'       // 타임아웃 (10분)
  | 'ABORTED_OFFLINE'       // 디바이스 오프라인 (대기 만료)
  | 'ABORTED_UNEXPECTED';   // 서버 통신 오류

// 세션 데이터 인터페이스
export interface MindHealthSession {
  sessionId: string;
  status: SessionStatus;
  startTime: string;
  endTime: string | null;
  deviceId: number;
  intervalSec: number;
  expectedTotalChunks: number;
  receivedChunkCount: number;
  notes: string | null;
}

// BCG 데이터 응답
export interface BCGDataResponse {
  bcgData: number[];
  chunkIndex: number;
  totalChunks: number | null;
  status: SessionStatus;
}

// 세션 목록 응답
export interface SessionListResponse {
  data: MindHealthSession[];
  pagination: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalEntries: number;
  };
}

// 세션 목록 조회 파라미터
export interface GetSessionsParams {
  status?: SessionStatus;
  pageNum?: number;
  pageSize?: number;
  sortBy?: string;
}

/**
 * 1. 세션 시작
 * POST /protectee/{protecteeId}/mind-health/sessions
 */
export const apiStartMindHealthSession = async (protecteeId: number) => {
  return await apiPost<MindHealthSession>(`/protectee/${protecteeId}/mind-health/sessions`);
};

export const useStartMindHealthSession = () => {
  return useMutation({
    mutationFn: (protecteeId: number) => apiStartMindHealthSession(protecteeId),
  });
};

/**
 * 2. 세션 목록 조회
 * GET /protectee/{protecteeId}/mind-health/sessions
 */
export const apiGetMindHealthSessions = async (
  protecteeId: number,
  params?: GetSessionsParams
) => {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append('status', params.status);
  if (params?.pageNum) queryParams.append('pageNum', params.pageNum.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  
  const url = `/protectee/${protecteeId}/mind-health/sessions${
    queryParams.toString() ? `?${queryParams.toString()}` : ''
  }`;
  
  return await apiGet<SessionListResponse>(url);
};

export const useGetMindHealthSessions = (
  protecteeId: number,
  params?: GetSessionsParams,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: QueryCacheKeys.mentalHealth.getSessions(protecteeId, params),
    queryFn: async () => {
      const { data } = await apiGetMindHealthSessions(protecteeId, params);
      return data;
    },
    enabled,
    retry: false,
  });
};

/**
 * 3. 특정 세션 상세 조회
 * GET /protectee/{protecteeId}/mind-health/sessions/{sessionId}
 */
export const apiGetMindHealthSession = async (
  protecteeId: number,
  sessionId: string
) => {
  return await apiGet<MindHealthSession>(
    `/protectee/${protecteeId}/mind-health/sessions/${sessionId}`
  );
};

export const useGetMindHealthSession = (
  protecteeId: number,
  sessionId: string,
  enabled: boolean = true,
  refetchInterval?: number
) => {
  return useQuery({
    queryKey: QueryCacheKeys.mentalHealth.getSession(protecteeId, sessionId),
    queryFn: async () => {
      const { data } = await apiGetMindHealthSession(protecteeId, sessionId);
      return data;
    },
    enabled,
    refetchInterval, // 폴링 지원
  });
};

/**
 * 4. 세션 취소
 * POST /protectee/{protecteeId}/mind-health/sessions/{sessionId}/cancel
 */
export const apiCancelMindHealthSession = async (
  protecteeId: number,
  sessionId: string
) => {
  return await apiPost<MindHealthSession>(
    `/protectee/${protecteeId}/mind-health/sessions/${sessionId}/cancel`
  );
};

export const useCancelMindHealthSession = () => {
  return useMutation({
    mutationFn: ({ protecteeId, sessionId }: { protecteeId: number; sessionId: string }) =>
      apiCancelMindHealthSession(protecteeId, sessionId),
  });
};

/**
 * 5. 세션 BCG 데이터 조회
 * GET /protectee/{protecteeId}/mind-health/sessions/{sessionId}/bcg-data
 */
export const apiGetSessionBCGData = async (
  protecteeId: number,
  sessionId: string
) => {
  return await apiGet<BCGDataResponse>(
    `/protectee/${protecteeId}/mind-health/sessions/${sessionId}/bcg-data`
  );
};

export const useGetSessionBCGData = (
  protecteeId: number,
  sessionId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: QueryCacheKeys.mentalHealth.getSessionBCGData(protecteeId, sessionId),
    queryFn: async () => {
      const { data } = await apiGetSessionBCGData(protecteeId, sessionId);
      return data;
    },
    enabled,
  });
};

